import React, { useState } from "react";
import styled, { keyframes } from "styled-components";
import { colors } from "utils";
import chatIcon from "assets/chat.svg";
import warningIcon from "assets/icons/lesson-report/warning.svg";
import loaderIcon from "assets/icons/live/loader.svg";
import { ReportModal } from "components/shared/ReportModal/ReportModal";
import { ResponsiveTabNav } from "../SessionSummaries/SessionReportTabs";
import { EndSessionButton } from "../SessionSummaries/OngoingSessionContent";
import { INCIDENTS_TAB_LABEL } from "../shared/criticalIncidentsUi";
import { LiveTranscriptTab } from "./LiveTranscriptTab";
import { LiveSafetyConcerns, useLiveTaggedQuotes } from "./LiveSafetyConcerns";

// The body a live pill opens to. Opens on Safety Concerns when the session has
// one; the tab is picked once on open, so a concern arriving mid-read adds the
// tab without pulling the teacher off what they are reading.
export const LiveSessionPanel = ({ id, session, transcriptLines, snapshotCount, onEndSession }) => {
  const sessionId = session.session_id;
  const raised = Boolean(session.safety_concern_raised);
  const [tab, setTab] = useState(() => (raised ? "incidents" : "summary"));
  const [reportOpen, setReportOpen] = useState(false);
  const [highlightQuote, setHighlightQuote] = useState(null);

  const concerns = useLiveTaggedQuotes(sessionId, raised, session.safety_concern_event_id);

  const tabs = [
    { key: "summary", label: "Session Summary" },
    ...(raised ? [{ key: "incidents", label: INCIDENTS_TAB_LABEL, icon: warningIcon }] : []),
    { key: "transcript", label: "Transcript" },
  ];
  const idPrefix = `live-${sessionId}`;
  const endSession = () => onEndSession?.();

  return (
    <Panel id={id}>
      <ResponsiveTabNav
        activeKey={tab}
        onSelect={setTab}
        tabs={tabs}
        idPrefix={idPrefix}
        ariaLabel="Live session sections"
      />
      <Body
        role="tabpanel"
        id={`${idPrefix}-tabpanel-${tab}`}
        aria-labelledby={`${idPrefix}-tab-${tab}`}
      >
        {tab === "transcript" ? (
          <LiveTranscriptTab
            sessionId={sessionId}
            streamedLines={transcriptLines}
            snapshotCount={snapshotCount}
            highlightQuote={highlightQuote}
          />
        ) : tab === "incidents" ? (
          <IncidentsLayout>
            {concerns.quotes.length > 0 ? (
              <LiveSafetyConcerns
                taggedQuotes={concerns.quotes}
                sessionStartedAt={session.session_started_at}
                onQuoteClick={({ text }) => {
                  // Live lines carry clock times, not the tagger's MM:SS, so
                  // the highlight matches on text alone.
                  setHighlightQuote({ text });
                  setTab("transcript");
                }}
              />
            ) : concerns.isInitialLoading ? (
              <Muted>Loading flagged quotes…</Muted>
            ) : concerns.isError ? (
              <Muted role="alert">
                We couldn't load the flagged quotes. The Transcript tab still has what was said.{" "}
                <RetryButton type="button" onClick={() => concerns.refetch()}>
                  Try again
                </RetryButton>
              </Muted>
            ) : (
              <Muted>A concern was flagged. Its quote is still being processed and will appear here shortly.</Muted>
            )}
            <EndSessionButton sessionId={sessionId} onSessionClosed={endSession} outline />
          </IncidentsLayout>
        ) : (
          <SummaryLayout>
            <SummaryHeader>
              <img src={loaderIcon} alt="" aria-hidden="true" />
              <SummaryTitle>We are supporting your students' math talk...</SummaryTitle>
            </SummaryHeader>
            <SummaryText>
              Check your Live Dashboard for alerts{" "}
              <InlineIcon src={warningIcon} alt="" aria-hidden="true" /> to see which groups may need your
              help. Your session report will be ready five minutes after the session ends.
            </SummaryText>
            <EndBlock>
              <SmallText>
                <Bold>Is the session actually over?</Bold>
                <br />
                Sometimes a student leaves a tab open, which keeps the session running.
              </SmallText>
              <EndSessionButton sessionId={sessionId} onSessionClosed={endSession} outline />
            </EndBlock>
          </SummaryLayout>
        )}
        <ReportButton type="button" onClick={() => setReportOpen(true)}>
          <img src={chatIcon} alt="" aria-hidden="true" />
          Report Issue or Give Feedback
        </ReportButton>
      </Body>
      <ReportModal
        show={reportOpen}
        onHide={() => setReportOpen(false)}
        defaultMessage="[Live Session Report] Issue reported from live session."
        reportLocation="Educator Dashboard - Live Session"
        extraPayload={{ session_id: sessionId }}
      />
    </Panel>
  );
};

const Panel = styled.div`
  background: #fff;
  border-top: 1px solid #eeece7;
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
`;

const IncidentsLayout = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 24px;

  & > :first-child {
    flex: 1;
    min-width: 0;
  }
`;

const SummaryLayout = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  text-align: center;
`;

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const SummaryHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;

  img {
    width: 48px;
    height: 48px;
    animation: ${spin} 1.6s linear infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    img {
      animation: none;
    }
  }
`;

const SummaryTitle = styled.h3`
  margin: 0;
  font-family: "ABC Marfa", sans-serif;
  font-size: 24px;
  font-weight: 600;
  line-height: 32px;
  color: #002e19;
`;

const SummaryText = styled.p`
  max-width: 600px;
  margin: -16px 0 0;
  font-family: Inter, sans-serif;
  font-size: 18px;
  line-height: 26px;
  letter-spacing: 0.25px;
  color: #002e19;
`;

const InlineIcon = styled.img`
  width: 24px;
  height: 24px;
  vertical-align: text-bottom;
`;

const EndBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const SmallText = styled.p`
  max-width: 600px;
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 14px;
  line-height: 20px;
  color: #002e19;
`;

const Bold = styled.span`
  font-weight: 700;
`;

const RetryButton = styled.button`
  padding: 0;
  background: none;
  border: none;
  color: ${colors.blue700};
  text-decoration: underline;
  cursor: pointer;
`;

const Muted = styled.p`
  margin: 16px 0 0;
  font-family: Inter, sans-serif;
  font-size: 16px;
  color: ${colors.nightlyWoods500};
`;

const ReportButton = styled.button`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  gap: 8px;
  padding: 0;
  background: none;
  border: none;
  font-family: Inter, sans-serif;
  font-size: 14px;
  line-height: 20px;
  color: #002e19;

  img {
    width: 16px;
    height: 16px;
  }

  &:hover {
    text-decoration: underline;
  }
`;
